import type { StandardLearningMode } from "@/lib/learning-mode";
import type { SessionLaunchSurface, SessionPrewarmPolicy } from "@/lib/sessionLaunchPresets";
import type { SessionMode } from "@/lib/sessionRouting";

type AuthIdentity = {
  id?: string | null;
  email?: string | null;
};

type UserMemoriesSnapshot = {
  memories: unknown[];
} | null;

type QdrantContextSnapshot = {
  languageName: string;
  languageLevel: string;
} | null;

type ConnectionRequestLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
};

export type ConnectionDetailsHeaders = Record<string, string>;

export type BuildConnectionDetailsRequestInput = {
  endpoint?: string;
  origin?: string;
  languageCode: string;
  languageName: string;
  languageLevel: string;
  voice: string;
  avatarId?: string;
  teachingStyle: string;
  nickname?: string;
  customInstructions?: string;
  noAvatar: boolean;
  sessionMode: SessionMode;
  learningMode?: StandardLearningMode;
  mathsDemoId?: string;
  mathsFixtureRevision?: string;
  tutorWhiteboardEnabled?: boolean;
  model: string;
  conversationId?: string;
  resumeFromConversationId?: string;
  journeyChapterId?: string | null;
  podcastDiscussionId?: string | null;
  sessionVocabularyParam?: string;
  fromLanguageName?: string;
  fromLanguageCode?: string;
  effectiveUser?: AuthIdentity | null;
  sessionUser?: AuthIdentity | null;
  contextUser?: AuthIdentity | null;
  memories?: UserMemoriesSnapshot;
  getMemoriesSummary: (maxCount?: number) => string;
  qdrantContext?: QdrantContextSnapshot;
  classId?: string;
  teacherPreferences?: unknown;
  tutorId?: string;
  tutorPrompt?: string;
  tutorCurriculumItemId?: string | null;
  launchSurface?: SessionLaunchSurface;
  prewarmPolicy?: SessionPrewarmPolicy;
  clientId: string;
  accessToken?: string;
  clientSubscriptionAccess?: boolean | null;
  clientTraceId?: string;
  startAttemptId?: string;
  clientStartMs?: number;
  logger?: ConnectionRequestLogger;
};

export type BuiltConnectionDetailsRequest = {
  url: URL;
  method: "POST";
  headers: ConnectionDetailsHeaders;
  body: string;
  preloadedMemoriesCount: number;
  preloadedQdrant: boolean;
};

export function buildConnectionDetailsRequest({
  endpoint = "/api/connection-details",
  origin = typeof window !== "undefined" ? window.location.origin : "",
  languageCode,
  languageName,
  languageLevel,
  voice,
  avatarId,
  teachingStyle,
  nickname,
  customInstructions,
  noAvatar,
  sessionMode,
  learningMode,
  mathsDemoId,
  mathsFixtureRevision,
  tutorWhiteboardEnabled = false,
  model,
  conversationId,
  resumeFromConversationId,
  journeyChapterId,
  podcastDiscussionId,
  sessionVocabularyParam,
  fromLanguageName,
  fromLanguageCode,
  effectiveUser,
  sessionUser,
  contextUser,
  memories,
  getMemoriesSummary,
  qdrantContext,
  classId,
  teacherPreferences,
  tutorId,
  tutorPrompt,
  tutorCurriculumItemId,
  launchSurface = "standard",
  prewarmPolicy = "normal",
  clientId,
  accessToken,
  clientSubscriptionAccess,
  clientTraceId,
  startAttemptId,
  clientStartMs,
  logger,
}: BuildConnectionDetailsRequestInput): BuiltConnectionDetailsRequest {
  const url = new URL(endpoint, origin);

  const body: Record<string, unknown> = {
    languageCode,
    languageName: languageName || languageCode,
    languageLevel,
    voice,
    teachingStyle,
    noAvatar,
    sessionMode,
    ...(learningMode ? { learningMode } : {}),
    tutorWhiteboardEnabled: tutorWhiteboardEnabled ? "true" : "false",
    model,
    clientTraceId: clientTraceId || "",
  };

  if (sessionMode === "maths") {
    body.mathsDemoId = mathsDemoId;
    if (mathsFixtureRevision) body.mathsFixtureRevision = mathsFixtureRevision;
    body.mathsWorkspaceProtocol = "2";
  }

  if (startAttemptId || clientTraceId) {
    body.startAttemptId = startAttemptId || clientTraceId;
  }

  if (avatarId) {
    body.avatarId = avatarId;
  }
  if (nickname) {
    body.nickname = nickname;
  }
  if (customInstructions) {
    body.customInstructions = customInstructions;
  }
  if (conversationId) {
    body.conversationId = conversationId;
  }
  if (resumeFromConversationId) {
    body.resumeFromConversationId = resumeFromConversationId;
  }
  if (journeyChapterId) {
    body.journeyChapterId = journeyChapterId;
  }
  if (podcastDiscussionId) {
    body.podcastDiscussionId = podcastDiscussionId;
  }
  if (sessionVocabularyParam) {
    body.vocabularyContext = sessionVocabularyParam;
  }

  if (fromLanguageName) {
    body.fromLanguageName = fromLanguageName;
  }
  if (fromLanguageCode) {
    body.fromLanguageCode = fromLanguageCode;
  }
  if (launchSurface !== "standard") {
    body.launchSurface = launchSurface;
  }
  if (prewarmPolicy === "fresh-on-start") {
    body.prewarmPolicy = prewarmPolicy;
  }

  let preloadedMemoriesCount = 0;
  let preloadedQdrant = false;

  if (effectiveUser?.email && sessionMode !== "maths") {
    logger?.info("Preparing authenticated connection details context", {
      hasUserId: Boolean(effectiveUser.id),
      source:
        sessionUser?.id === effectiveUser.id
          ? "session"
          : contextUser?.id === effectiveUser.id
            ? "context"
            : "unknown",
    });

    if (memories && memories.memories.length > 0) {
      preloadedMemoriesCount = memories.memories.length;
      body.preloadedMemoriesCount = memories.memories.length;

      const summary = getMemoriesSummary(10);
      if (summary && summary.length > 0) {
        body.memoriesSummary = summary.slice(0, 2000);
        logger?.info(`Passing ${memories.memories.length} pre-loaded memories to agent`);
      }
    }

    if (
      qdrantContext &&
      qdrantContext.languageName === languageName &&
      qdrantContext.languageLevel === languageLevel
    ) {
      preloadedQdrant = true;
      body.preloadedQdrantContext = "true";
      logger?.info(`Qdrant context pre-loaded for ${languageName}`);
    }

    if (classId) {
      body.classId = classId;
      logger?.info(`Class ID added to connection request: ${classId}`);
    }

    if (teacherPreferences) {
      try {
        body.teacherPreferences = JSON.stringify(teacherPreferences);
        logger?.info("Teacher preferences added to connection request", {
          keys: Object.keys(teacherPreferences || {}),
        });
      } catch (error) {
        logger?.warn("Failed to serialize teacherPreferences", { error });
      }
    }
  }

  if (tutorId) {
    body.tutorId = tutorId;
    logger?.info(`Tutor ID added to connection request: ${tutorId}`);
  }
  if (tutorCurriculumItemId) {
    body.tutorCurriculumItemId = tutorCurriculumItemId;
    logger?.info("Tutor Journey lesson added to connection request", {
      tutorCurriculumItemId,
    });
  }
  if (tutorPrompt && !tutorId) {
    body.tutorPrompt = tutorPrompt;
    logger?.info(`Providing custom tutor prompt override (${tutorPrompt.length} chars)`);
  }

  if (typeof clientStartMs === "number") {
    body.clientStartMs = clientStartMs;
  }
  if (clientSubscriptionAccess === true) {
    body.clientReportedSubscriptionStatus = "active";
  }

  const headers: ConnectionDetailsHeaders = {
    "Content-Type": "application/json",
    "x-client-id": clientId,
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return {
    url,
    method: "POST",
    headers,
    body: JSON.stringify(body),
    preloadedMemoriesCount,
    preloadedQdrant,
  };
}
